import React, { useCallback, useEffect, useState } from "react";
import { useOnboardingStyles } from "./styles";
import { useResource } from "rest-hooks";
import { OptionPairResource } from "../../../resources";
import {
	FormControl,
	FormControlLabel,
	FormHelperText,
	FormLabel,
	Grid,
	MenuItem,
	Radio,
	RadioGroup,
	TextField,
} from "@material-ui/core";
import { useCommonWidths } from "../../../hooks";
import { KycDataModel } from "../../../resources";
import { OnboardingHeader } from "./OnboardingHeader";
import { Autocomplete } from "@material-ui/lab";
import { CommonOnboardingProps } from "./Onboarding";
import { NavigationButtons } from "./NavigationButtons";

type InputField = { required: boolean; value: string };

type KycFormModel = {
	pepCheck: InputField;
	pepRole: InputField;
	vehicleUsage: InputField;
	paymentsOrigin: InputField;
	paymentsFromAbroadCheck: InputField;
	paymentsFromAbroadCountry: InputField;
	partialPaymentsOver100K: InputField;
};

export const OnboardingKYC = (props: CommonOnboardingProps) => {
	const {
		completeOnboardingPage,
		navigateBack,
		onboardingStateHandler,
		isLastStep,
	} = props;

	const vehicleUsages = useResource(OptionPairResource.listShape(), {
		name: "KYC_VehicleCustomerUsage",
	});
	const nationalities = useResource(OptionPairResource.listShape(), {
		name: "Nationality",
	});
	const paymentOrigins = useResource(OptionPairResource.listShape(), {
		name: "KYC_PaymentsOrigin",
	});

	const classes = useOnboardingStyles();
	const isMobile = useCommonWidths().isSmDown;

	const [errors, setErrors] = useState<KycDataModel>({
		pepCheck: "",
		pepRole: "",
		vehicleUsage: "",
		paymentsOrigin: "",
		paymentsFromAbroadCheck: "",
		paymentsFromAbroadCountry: "",
		partialPaymentsOver100K: "",
	});
	const [kycData, setKycData] = useState<KycFormModel>({
		pepCheck: {
			required: true,
			value: onboardingStateHandler.state.kyc.pepCheck,
		},
		pepRole: {
			required: false,
			value: onboardingStateHandler.state.kyc.pepRole,
		},
		vehicleUsage: {
			required: true,
			value: onboardingStateHandler.state.kyc.vehicleUsage,
		},
		paymentsOrigin: {
			required: true,
			value: onboardingStateHandler.state.kyc.paymentsOrigin,
		},
		paymentsFromAbroadCheck: {
			required: true,
			value: onboardingStateHandler.state.kyc.paymentsAbroadCheck,
		},
		paymentsFromAbroadCountry: {
			required: false,
			value: onboardingStateHandler.state.kyc.paymentsAbroadCountry,
		},
		partialPaymentsOver100K: {
			required: true,
			value: onboardingStateHandler.state.kyc.partialPaymentsAbove100K,
		},
	});

	const handleChangeManual = (fieldName: string, newValue: string) => {
		const key = fieldName as keyof KycFormModel;
		setKycData({
			...kycData,
			[key]: { ...kycData[key], value: newValue },
		});

		// Clearing errors on input:
		let updatedErrors = { ...errors };
		if (newValue.match("[a-zA-Z]+")) {
			updatedErrors = { ...updatedErrors, [key]: "" };
		}
		if (key === "pepCheck" && newValue === "false") {
			updatedErrors = { ...updatedErrors, pepRole: "" };
		} else if (key === "paymentsFromAbroadCheck" && newValue === "false") {
			updatedErrors = { ...updatedErrors, paymentsFromAbroadCountry: "" };
		}
		setErrors(updatedErrors);
	};

	const formHasErrors = useCallback((): boolean => {
		let hasErrors = false;
		const missingFields = Object.entries(kycData).filter((x) => {
			const { required, value } = x[1];
			return required && !value;
		});
		let newErrors = { ...errors };
		if (missingFields.length > 0) {
			missingFields.forEach((entry) => {
				const fieldName = entry[0];
				newErrors = { ...newErrors, [fieldName]: "Vennligst fyll ut" };
			});
			hasErrors = true;
		}
		if (
			kycData.pepCheck.value === "true" &&
			!kycData.pepRole.value?.match("[a-zA-Z]+")
		) {
			newErrors = { ...newErrors, pepRole: "Vennligst fyll ut hvilket verv" };
			hasErrors = true;
		}
		if (
			kycData.paymentsFromAbroadCheck.value === "true" &&
			!kycData.paymentsFromAbroadCountry.value?.match("[a-zA-Z]+")
		) {
			newErrors = {
				...newErrors,
				paymentsFromAbroadCountry: "Vennligst fyll ut hvilket land",
			};
			hasErrors = true;
		}
		setErrors(newErrors);
		return hasErrors;
	}, [errors, kycData]);

	const handleSubmit = async () => {
		if (formHasErrors()) return;

		completeOnboardingPage();
	};

	// Update `formStateHandler`'s KYC state from <Onboarding /> component whenever 'kycData' changes
	useEffect(() => {
		onboardingStateHandler.update({
			...onboardingStateHandler.state,
			kyc: {
				partialPaymentsAbove100K: kycData.partialPaymentsOver100K.value,
				paymentsAbroadCheck: kycData.paymentsFromAbroadCheck.value,
				paymentsAbroadCountry: kycData.paymentsFromAbroadCountry.value,
				paymentsOrigin: kycData.paymentsOrigin.value,
				pepCheck: kycData.pepCheck.value,
				pepRole: kycData.pepRole.value,
				vehicleUsage: kycData.vehicleUsage.value,
			},
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [kycData]); // [kycData] should be the only dependcy

	return (
		<Grid container spacing={3}>
			<OnboardingHeader
				isMobile={isMobile}
				subtitle="Vi trenger å vite litt mer om deg før du kan gå videre"
			/>
			<div className={classes.formWrapper}>
				<Grid container item xs={12} direction="column" alignItems="center">
					<Grid
						container
						item
						xs={isMobile ? 12 : 8}
						direction="column"
						spacing={3}
					>
						<Grid item xs={12}>
							<FormControl error={Boolean(errors.pepCheck)} fullWidth>
								<FormLabel className={classes.fieldLabel}>
									Er du PEP (politisk eksponert person)?
								</FormLabel>
								<RadioGroup
									name="pepCheck"
									value={kycData.pepCheck.value}
									row
									onChange={(e, value) =>
										handleChangeManual(e.target.name, value)
									}
								>
									<FormControlLabel
										value={"true"}
										control={<Radio />}
										label="Ja"
									/>
									<FormControlLabel
										value={"false"}
										control={<Radio />}
										label="Nei"
									/>
								</RadioGroup>
								<FormHelperText>{errors.pepCheck}</FormHelperText>
							</FormControl>

							{kycData.pepCheck.value === "true" && (
								<TextField
									name="pepRole"
									label="Hvilket verv har du?"
									InputLabelProps={{ className: classes.fieldLabel }}
									type="text"
									multiline
									value={kycData.pepRole.value}
									rows={2}
									error={Boolean(errors.pepRole)}
									helperText={errors.pepRole ?? ""}
									variant="outlined"
									fullWidth
									onChange={(e) =>
										handleChangeManual(e.target.name, e.target.value)
									}
								/>
							)}
						</Grid>
						<Grid item xs={12}>
							<TextField
								name="vehicleUsage"
								label="Hva benyttes kjøretøyet/ panteobjektet til?"
								InputLabelProps={{
									className: classes.fieldLabel,
									shrink: isMobile || Boolean(kycData.vehicleUsage.value),
								}}
								value={kycData.vehicleUsage.value || ""}
								error={Boolean(errors.vehicleUsage)}
								helperText={errors.vehicleUsage ?? ""}
								variant="outlined"
								fullWidth
								onChange={(e) =>
									handleChangeManual(e.target.name, e.target.value)
								}
								select
							>
								{vehicleUsages.map((item) => (
									<MenuItem key={item.value} value={item.value}>
										{item.text}
									</MenuItem>
								))}
							</TextField>
						</Grid>
						<Grid item xs={12}>
							<TextField
								name="paymentsOrigin"
								label="Hvor kommer dine innbetalinger på lånet fra?"
								InputLabelProps={{
									className: classes.fieldLabel,
									shrink: isMobile || Boolean(kycData.paymentsOrigin.value),
								}}
								value={kycData.paymentsOrigin.value || ""}
								error={Boolean(errors.paymentsOrigin)}
								helperText={errors.paymentsOrigin ?? ""}
								variant="outlined"
								fullWidth
								onChange={(e) =>
									handleChangeManual(e.target.name, e.target.value)
								}
								select
							>
								{paymentOrigins.map((item) => (
									<MenuItem key={item.value} value={item.value}>
										{item.text}
									</MenuItem>
								))}
							</TextField>
						</Grid>
						<Grid item xs={12}>
							<FormControl error={Boolean(errors.paymentsFromAbroadCheck)}>
								<FormLabel className={classes.fieldLabel}>
									Vil du foreta innbetalinger på ditt lån fra utenlandsk konto?
								</FormLabel>
								<RadioGroup
									name="paymentsFromAbroadCheck"
									value={kycData.paymentsFromAbroadCheck.value}
									row
									onChange={(e, value) =>
										handleChangeManual(e.target.name, value)
									}
								>
									<FormControlLabel
										value={"true"}
										control={<Radio />}
										label="Ja"
									/>
									<FormControlLabel
										value={"false"}
										control={<Radio />}
										label="Nei"
									/>
								</RadioGroup>
								<FormHelperText>
									{errors.paymentsFromAbroadCheck}
								</FormHelperText>
							</FormControl>
							{kycData.paymentsFromAbroadCheck.value === "true" && (
								<FormControl
									error={Boolean(errors.paymentsFromAbroadCountry)}
									fullWidth
								>
									<Autocomplete
										options={nationalities}
										onChange={(e, val) =>
											handleChangeManual(
												"paymentsFromAbroadCountry",
												val?.value || ""
											)
										}
										getOptionLabel={(opt) => opt.text}
										renderInput={(params) => {
											return (
												<TextField
													{...params}
													variant="outlined"
													label="Hvilket land?"
													inputProps={{
														...params.inputProps,
														className: classes.fieldLabel,
													}}
													InputLabelProps={{
														className: classes.fieldLabel,
													}}
												/>
											);
										}}
										value={
											nationalities.find(
												(x) =>
													x.value === kycData.paymentsFromAbroadCountry.value
											) || undefined
										}
									/>
									<FormHelperText>
										{errors.paymentsFromAbroadCountry ?? ""}
									</FormHelperText>
								</FormControl>
								// <TextField
								// 	name="paymentsFromAbroadCountry"
								// 	label="Hvilket land?"
								// 	InputLabelProps={{
								// 		className: classes.fieldLabel,
								// 		shrink: isMobile || Boolean(kycData2.paymentsFromAbroadCountry.value)
								// 	}}
								// 	value={kycData2.paymentsFromAbroadCountry.value || ""}
								// 	error={Boolean(errors.paymentsFromAbroadCountry)}
								// 	helperText={errors.paymentsFromAbroadCountry ?? ""}
								// 	variant="outlined"
								// 	fullWidth
								// 	onChange={(e) => handleChangeManual(e.target.name, e.target.value)}
								// 	select
								// >
								// 	{nationalities.map(item => <MenuItem key={item.value} value={item.value}>{item.text}</MenuItem>)}
								// </TextField>
							)}
						</Grid>
						<Grid item xs={12}>
							<FormControl error={Boolean(errors.partialPaymentsOver100K)}>
								<FormLabel className={classes.fieldLabel}>
									Har du planer om å nedbetale lånet med delsummer over kr.
									100.000,-?
								</FormLabel>
								<RadioGroup
									name="partialPaymentsOver100K"
									value={kycData.partialPaymentsOver100K.value}
									row
									onChange={(e, value) =>
										handleChangeManual(e.target.name, value)
									}
								>
									<FormControlLabel
										value={"true"}
										control={<Radio />}
										label="Ja"
									/>
									<FormControlLabel
										value={"false"}
										control={<Radio />}
										label="Nei"
									/>
								</RadioGroup>
								<FormHelperText>
									{errors.partialPaymentsOver100K}
								</FormHelperText>
							</FormControl>
						</Grid>
					</Grid>

					<NavigationButtons
						handleNavigateNext={handleSubmit}
						nextDisabled={Object.values(errors).filter((x) => x).length > 0}
						prevBtnHidden={false}
						handleNavigatePrev={navigateBack}
						isLastStep={isLastStep}
					/>
				</Grid>
			</div>
		</Grid>
	);
};
